import { cineamoApiClient } from '../../apiClient';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { CinemaRoomInputDto } from '../../models/cinema-room/CinemaRoomInputDto';
import { CinemaRoomShapeDto } from '../../models/cinema-room-shape/CinemaRoomShapeDto.types';
import { CinemaRoomShapeInputDto } from '../../models/cinema-room-shape/CinemaRoomShapeInputDto';
import { CinemaRoomImageListResponse, CinemaRoomListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Cinema Rooms ------------------------>

type GetCinemaRoomsParams = UrlQueryParameter & {
    cinemaId?: number | string;
};

/**
 * CIN-14426: an id that never resolved (`NaN` from an id that was not loaded yet, or an empty
 * string) is omitted from the query string, which would widen a request from one cinema to every
 * cinema. Answer like a failed request instead of returning a foreign cinema's rooms.
 */
function isResolvedId(id?: number | string): boolean {
    if (typeof id === 'number') {
        return Number.isFinite(id);
    }
    return typeof id === 'string' && id.trim().length > 0;
}

/**
 * This endpoint returns a list of cinema rooms.
 *
 * @example
 * // Get a list of cinema rooms by cinema:
 * getCinemaRooms({...UrlQueryParameter});
 *
 * @param urlParams
 * @returns {ApiResponse<unknown, CinemaRoomListResponse>}
 */

export async function getCinemaRooms(
    urlParams?: GetCinemaRoomsParams
): Promise<ApiResponse<unknown, CinemaRoomListResponse> | null> {
    if (urlParams && 'cinemaId' in urlParams && !isResolvedId(urlParams.cinemaId)) {
        return null;
    }
    return cineamoApiClient
        .get<ApiResponse<unknown, CinemaRoomListResponse>>(`/cinema-rooms${buildUrlQuery(urlParams)}`)
        .then((response) => {
            return response.data;
        });
}

/**
 * Returns the cinema rooms of one cinema. The cinema is part of the path, so the scope cannot be
 * overridden by a query param — prefer this over `getCinemaRooms({ cinemaId })`.
 *
 * @example
 * // Get all cinema rooms of cinema 7:
 * getCinemaRoomsByCinemaId(7);
 *
 * @param cinemaId
 * @param urlParams
 * @returns {ApiResponse<unknown, CinemaRoomListResponse>}
 */

export async function getCinemaRoomsByCinemaId(
    cinemaId?: number | string,
    urlParams?: UrlQueryParameter
): Promise<ApiResponse<unknown, CinemaRoomListResponse> | null> {
    // An unresolved id would request `/cinemas/NaN/cinema-rooms` once per render, and the API can
    // only answer 404. Same reasoning as the CIN-14426 guard on getCinemaRooms above; here it costs
    // a pointless round trip rather than a widened scope, because the path cannot widen.
    if (!isResolvedId(cinemaId)) {
        return null;
    }

    return cineamoApiClient
        .get<
            ApiResponse<unknown, CinemaRoomListResponse>
        >(`/cinemas/${cinemaId}/cinema-rooms${buildUrlQuery(urlParams)}`)
        .then((response) => {
            return response.data;
        });
}

// <------------------------ Cinema Room ------------------------>

/**
 * This endpoint returns a cinema room.
 *
 * @example
 * // Get a cinema room by id:
 * getCinemaRoomById(1);
 *
 * @param id
 * @returns {ApiResponse<CinemaRoomDto>}
 */

export async function getCinemaRoomById(id?: number | string): Promise<ApiResponse<CinemaRoomDto> | null> {
    // A room that was never resolved would request `/cinema-rooms/undefined`, which the API can
    // only answer 404 — spend no round trip on it. Same guard as the room list functions above.
    if (!isResolvedId(id)) {
        return null;
    }

    return cineamoApiClient.get<ApiResponse<CinemaRoomDto>>(`/cinema-rooms/${id}`).then((response) => {
        return response.data;
    });
}

// <------------------------ Create Cinema Room ------------------------>

/**
 * This endpoint returns a cinema room.
 *
 * @example
 * // Create a cinema room:
 * createCinemaRoom({...cinemaRoom});
 *
 * @param cinemaRoom
 * @returns {ApiResponse<CinemaRoomDto>} rejects with the `ApiError` on a non-2xx
 */

export async function createCinemaRoom(cinemaRoom: CinemaRoomInputDto): Promise<ApiResponse<CinemaRoomDto>> {
    return cineamoApiClient.post<ApiResponse<CinemaRoomDto>>('/cinema-rooms', cinemaRoom).then((response) => {
        return response.data;
    });
}

// <------------------------ Update Cinema Room ------------------------>

/**
 * This endpoint returns a cinema room.
 *
 * @example
 * // Update a cinema room:
 * updateCinemaRoomById(1, {...cinemaRoom});
 *
 * @param cinemaRoom
 * @param cinemaRoomId
 * @returns {ApiResponse<CinemaRoomDto>} rejects with the `ApiError` on a non-2xx
 */

export async function updateCinemaRoomById(
    cinemaRoomId: number | string,
    cinemaRoom: CinemaRoomInputDto
): Promise<ApiResponse<CinemaRoomDto>> {
    return cineamoApiClient
        .patch<ApiResponse<CinemaRoomDto>>(`/cinema-rooms/${cinemaRoomId}`, cinemaRoom)
        .then((response) => {
            return response.data;
        });
}

// <------------------------ Delete Cinema Room ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a cinema room by id:
 * deleteCinemaRoomById(1);
 *
 * @param cinemaRoom
 * @returns {boolean}
 */

export async function deleteCinemaRoomById(cinemaRoom: number | string): Promise<boolean> {
    return cineamoApiClient.delete(`/cinema-rooms/${cinemaRoom}`).then((response) => {
        return response.status === 204;
    });
}

// <------------------------ Set Cinema Room Shape ------------------------>

export async function setCinemaRoomShape(
    cinemaRoomId: number,
    roomShape: CinemaRoomShapeInputDto
): Promise<ApiResponse<CinemaRoomShapeDto>> {
    return cineamoApiClient
        .put<ApiResponse<CinemaRoomShapeDto>>(`/cinema-rooms/${cinemaRoomId}/shape`, roomShape)
        .then((response) => response.data);
}

export async function getCinemaRoomImages(
    cinemaRoomId: CinemaRoomDto['id'],
    urlParams?: UrlQueryParameter
): Promise<ApiResponse<unknown, CinemaRoomImageListResponse>> {
    return cineamoApiClient
        .get<
            ApiResponse<unknown, CinemaRoomImageListResponse>
        >(`/cinema-rooms/${cinemaRoomId}/images${buildUrlQuery(urlParams)}`)
        .then((response) => {
            return response.data;
        });
}
